import type { Output } from "@/kernel/Kernel";
import { outputText } from "@/kernel/outputs";
import { src, type NbCell, type Task, type TaskResult, type TaskStatus } from "@/kernel/runner";
import { CodeBlock, CopyButton } from "@/ui/CodeBlock";
import { STATUS_WORD } from "./AutocheckGrid";

const dot: Record<TaskStatus, string> = {
  passed: "bg-ok",
  failed: "bg-err",
  idle: "border border-bd2 bg-transparent",
};
const fg: Record<TaskStatus, string> = { passed: "text-ok", failed: "text-err", idle: "text-ink2" };

const ANSI = /\x1b\[[0-9;]*m/g;

function images(outputs: Output[]): string[] {
  const out: string[] = [];
  for (const o of outputs) {
    if (o.output_type !== "display_data" && o.output_type !== "execute_result") continue;
    const png = o.data["image/png"];
    if (png) out.push(Array.isArray(png) ? png.join("") : png);
  }
  return out;
}

/** Задача в режиме чтения: код (без редактора и запуска) и последний вывод. */
export function TaskBlock({ task, code, checks, result }: { task: Task; code?: NbCell; checks?: NbCell; result?: TaskResult }) {
  const status: TaskStatus = result?.status ?? "idle";
  const outputs = [...(code?.outputs ?? []), ...(checks?.outputs ?? [])];
  const stored = outputText(outputs).replace(ANSI, "");
  // Черновик мог сохранить статус без выводов — тогда показываем причину из результата.
  const text = stored || (status === "failed" && result?.error ? `Задача ${task.n}: ${result.error}` : "");
  const pngs = images(outputs);
  const failed = status === "failed";

  return (
    <section className="flex flex-col gap-2" data-testid={`task-${task.n}`} data-status={status}>
      <div className="flex items-baseline gap-2.5">
        <h2 className="m-0 min-w-0 flex-1 font-mono text-[12.5px] font-semibold leading-[1.4] text-ink [overflow-wrap:anywhere]">
          Задача {task.n} · {task.title}
        </h2>
        <span className={`inline-flex flex-none items-center gap-1.5 font-mono text-[11px] ${fg[status]}`}>
          <span aria-hidden className={`size-1.5 rounded-full ${dot[status]}`} />
          {STATUS_WORD[status]}
        </span>
      </div>
      <div className="overflow-hidden rounded-md border border-bd bg-bg2">
        <div className="flex items-center gap-2 border-b border-bd bg-card px-3 py-1.5 font-mono text-[10.5px] text-ink2">
          <span>In [{task.n}] · только чтение</span>
          <span className="flex-1" />
          {code && <CopyButton text={src(code)} />}
        </div>
        {code ? (
          <div className="[&_code]:whitespace-pre-wrap [&_code]:[overflow-wrap:anywhere]">
            <CodeBlock code={src(code)} flat />
          </div>
        ) : (
          <p className="m-0 px-3 py-2.5 text-[12px] text-ink2">В ноутбуке нет ячейки этой задачи.</p>
        )}
        {text && (
          <pre
            className={`m-0 max-h-60 overflow-auto whitespace-pre-wrap border-t border-bd px-3 py-[11px] font-mono text-[11px] leading-[1.5] [overflow-wrap:anywhere] ${failed ? "bg-errsoft text-err" : "bg-card text-ink2"}`}
            data-testid="task-output"
          >
            {text}
          </pre>
        )}
        {pngs.map((b64, i) => (
          <img key={i} src={`data:image/png;base64,${b64}`} alt={`график задачи ${task.n}`} className="block max-w-full border-t border-bd bg-card" />
        ))}
      </div>
    </section>
  );
}
